import React from "react";
import Button from "components/Button/Regular";
import Input from "components/Input/Input";
import Select from "components/Input/Select";
import {
  activationOptions,
  configTypeOptions,
  prepaymentOptions,
  queueOptions,
  ACTIVATION_STATE,
  CONFIG_TYPE,
  PREPAYMENT_REQUIRED,
  QUEUE_ACTIVE,
} from "constants/bookingEnums";
import { BookingConfig } from "services/booking-configs.service";

export type BookingConfigFormValues = {
  name: string;
  isActive: number;
  numberOfRequiredResources: number;
  prePaymentRequired: number;
  monetaryAmount: number;
  maxOrdersPerDay: number;
  maxDailyLimit: number;
  configType: number;
  isQueueActive: number;
};

type Props = {
  open: boolean;
  /** Present when editing, absent when creating. */
  existing?: BookingConfig | null;
  busy?: boolean;
  onCancel: () => void;
  onSubmit: (values: BookingConfigFormValues) => void;
};

/**
 * Every Core enum here starts at 1 = Unknown, so an unset field does not fall
 * back to something sensible - it stores Unknown. Defaults are explicit.
 */
const EMPTY: BookingConfigFormValues = {
  name: "",
  isActive: ACTIVATION_STATE.ACTIVE,
  // Always zero. Resources are attached to a configuration afterwards,
  // through the resource map, and anything else is refused outright.
  numberOfRequiredResources: 0,
  prePaymentRequired: PREPAYMENT_REQUIRED.NOT_REQUIRED,
  monetaryAmount: 0,
  maxOrdersPerDay: 0,
  maxDailyLimit: 0,
  configType: CONFIG_TYPE.BASIC,
  isQueueActive: QUEUE_ACTIVE.NOT_ACTIVE,
};

const BookingConfigDialog = ({
  open,
  existing,
  busy,
  onCancel,
  onSubmit,
}: Props) => {
  const [values, setValues] = React.useState<BookingConfigFormValues>(EMPTY);
  const [error, setError] = React.useState<string | null>(null);

  // Reset whenever the dialog opens, otherwise the previous config's values
  // linger and an edit can silently carry fields from the last one.
  React.useEffect(() => {
    if (!open) return;
    setError(null);
    setValues(
      existing
        ? {
            name: existing.name,
            isActive: existing.isActive || ACTIVATION_STATE.ACTIVE,
            numberOfRequiredResources: 0,
            prePaymentRequired:
              existing.prePaymentRequired || PREPAYMENT_REQUIRED.NOT_REQUIRED,
            monetaryAmount: existing.monetaryAmount ?? 0,
            maxOrdersPerDay: existing.maxOrdersPerDay ?? 0,
            maxDailyLimit: existing.maxDailyLimit ?? 0,
            configType: existing.configType || CONFIG_TYPE.BASIC,
            isQueueActive: existing.isQueueActive || QUEUE_ACTIVE.NOT_ACTIVE,
          }
        : EMPTY
    );
  }, [open, existing]);

  if (!open) return null;

  const set = <K extends keyof BookingConfigFormValues>(
    key: K,
    value: BookingConfigFormValues[K]
  ) => setValues((v) => ({ ...v, [key]: value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!values.name.trim()) {
      setError("Give the configuration a name.");
      return;
    }
    setError(null);
    onSubmit({ ...values, name: values.name.trim() });
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-6">
      <button
        type="button"
        aria-label="Close dialog"
        className="absolute inset-0 bg-slate-900/35 backdrop-blur-[2px]"
        onClick={() => {
          if (!busy) onCancel();
        }}
      />

      <form
        onSubmit={handleSubmit}
        className="relative max-h-full w-full max-w-lg overflow-y-auto rounded-3xl border border-slate-200 bg-white shadow-[0_18px_50px_-12px_rgba(15,23,42,0.28)]"
      >
        <div className="border-b border-slate-100 bg-slate-50/70 px-6 py-5">
          <h3 className="text-lg font-semibold text-slate-900">
            {existing ? "Edit booking configuration" : "Add booking configuration"}
          </h3>
          <p className="mt-1 text-sm leading-6 text-slate-600">
            Controls how this service can be booked. Resources and their durations are set per resource
            on the configuration, not here.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 px-6 py-5 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Input
              label="Name"
              placeholder="e.g. Standard consultation"
              value={values.name}
              onChange={(e) => set("name", e.target.value)}
            />
          </div>

          <Select
            label="Status"
            options={activationOptions}
            value={String(values.isActive)}
            onChange={(e) => set("isActive", Number(e.target.value))}
          />

          <Select
            label="Type"
            options={configTypeOptions}
            value={String(values.configType)}
            onChange={(e) => set("configType", Number(e.target.value))}
          />

          <Input
            type="number"
            label="Price"
            value={String(values.monetaryAmount)}
            onChange={(e) => set("monetaryAmount", Number(e.target.value))}
          />

          <Input
            type="number"
            label="Max bookings per day"
            placeholder="0 for no limit"
            value={String(values.maxOrdersPerDay)}
            onChange={(e) => set("maxOrdersPerDay", Number(e.target.value))}
          />

          <Input
            type="number"
            label="Daily limit"
            placeholder="0 for no limit"
            value={String(values.maxDailyLimit)}
            onChange={(e) => set("maxDailyLimit", Number(e.target.value))}
          />

          <Select
            label="Queue"
            options={queueOptions}
            value={String(values.isQueueActive)}
            onChange={(e) => set("isQueueActive", Number(e.target.value))}
          />


          <Select
            label="Payment"
            options={prepaymentOptions}
            value={String(values.prePaymentRequired)}
            onChange={(e) => set("prePaymentRequired", Number(e.target.value))}
          />

          {error && (
            <p className="sm:col-span-2 text-sm text-red-500">{error}</p>
          )}
        </div>

        <div className="flex flex-wrap justify-end gap-3 border-t border-slate-100 px-6 py-4">
          <Button color="Secondary" onClick={onCancel} disabled={busy}>
            <span>Cancel</span>
          </Button>
          <Button color="Primary" type="submit" disabled={busy}>
            <span>{busy ? "Saving…" : existing ? "Save changes" : "Add configuration"}</span>
          </Button>
        </div>
      </form>
    </div>
  );
};

export default BookingConfigDialog;
